import type { Vector3 } from 'three';
import { ONE_SHOTS, bed, seeded, work, type CueCtx, type CueId, type Loop, type WorkKind } from './cues';

/**
 * THE MIX.
 *
 * Three buses and a listener. Everything the player does goes to the head bus,
 * unpanned, because it is happening inside their own skull and a footstep that
 * pans to the left because the camera turned is a footstep taken by somebody
 * else. Everything anyone else does goes through a panner. The bed sits under
 * both and never moves.
 *
 * The compressor across the master is not there for loudness. It is there
 * because an angle grinder, an alarm and three guards' radios can all be
 * running at the same time, and without it that moment — which is the most
 * important moment in the game — is the one moment the mix falls apart.
 *
 * Occlusion is a low-pass and nothing more. There is no reverb send, no
 * portalling, no diffraction. A single filter whose cutoff drops from twenty
 * kilohertz to eight hundred when a wall is in the way buys the entire gameplay
 * value — you can tell whether the guard you can hear is in this room or the
 * next one — for one raycast every fifth of a second, and everything past that
 * costs CPU the renderer has already spent.
 */

/** How many world sounds can be in flight at once. */
const POOL = 24;
/** Cutoff when a wall is in the way, in Hz. Low enough to hear as "elsewhere". */
const OCCLUDED_HZ = 700;
const OPEN_HZ = 20000;

export interface Emitter {
  readonly input: GainNode;
  /** 0 clear, 1 fully behind geometry. Smoothed toward, never snapped. */
  occlusion: number;
  set(x: number, y: number, z: number): void;
}

interface PoolSlot extends Emitter {
  panner: PannerNode;
  lp: BiquadFilterNode;
  freeAt: number;
  current: number;
}

export class AudioEngine {
  readonly ctx: AudioContext;
  /** Where world sounds land, after their panner. */
  private readonly worldBus: GainNode;
  /** Unpanned, for the player's own body and the UI. */
  readonly head: GainNode;
  private readonly bedBus: GainNode;
  private readonly master: GainNode;
  private readonly slots: PoolSlot[] = [];
  private readonly named = new Map<string, PoolSlot>();
  private readonly rng = seeded(0xa17d);
  private bedLoop: Loop | null = null;
  private started = false;
  /**
   * Every cue ever started, by id. Diagnostics only, and cheap enough to always
   * be on: a Map of thirty-odd integers touched a few times a second.
   *
   * It is here because "the audio works" is otherwise unfalsifiable from
   * outside. A context can be running, a bed can be playing, and the game can
   * still be making no sound at all — and the voice pool cannot tell you,
   * because the cues the player hears most are head-relative and never take a
   * pool slot. A count per id is the only honest answer to "what did that
   * minute of play actually sound like", and it is what the QA leg asserts on.
   */
  readonly fired = new Map<string, number>();

  constructor() {
    // `latencyHint: 'interactive'` asks the platform for the smallest buffer it
    // will give. A footstep that lands 80ms after the foot does is a footstep
    // the player reads as somebody else's.
    const Ctor: typeof AudioContext =
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ?? AudioContext;
    this.ctx = new Ctor({ latencyHint: 'interactive' });

    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 22;
    comp.ratio.value = 6;
    comp.attack.value = 0.004;
    comp.release.value = 0.22;
    this.master.connect(comp);
    comp.connect(this.ctx.destination);

    this.worldBus = this.ctx.createGain();
    this.worldBus.gain.value = 1;
    this.worldBus.connect(this.master);
    this.head = this.ctx.createGain();
    this.head.gain.value = 0.85;
    this.head.connect(this.master);
    this.bedBus = this.ctx.createGain();
    this.bedBus.gain.value = 0.5;
    this.bedBus.connect(this.master);

    for (let i = 0; i < POOL; i++) this.slots.push(this.makeSlot());

    // A browser will not let a page make noise before it has been touched, and
    // it is right to. Rather than fail silently, the context is built suspended
    // and woken by the first input of any kind — which in this game is the click
    // that captures the pointer, so the player never notices there was a step.
    const wake = () => {
      void this.ctx.resume();
      if (this.ctx.state === 'running') {
        window.removeEventListener('pointerdown', wake);
        window.removeEventListener('keydown', wake);
      }
    };
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', wake);
  }

  private makeSlot(): PoolSlot {
    const input = this.ctx.createGain();
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = OPEN_HZ;
    const panner = this.ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    // Two metres of full volume, then a gentle roll-off out to forty. The mall
    // is eighty metres end to end and a guard's boots have to be inaudible from
    // the far side of it without being inaudible across a shop unit.
    panner.refDistance = 2.2;
    panner.rolloffFactor = 1.15;
    panner.maxDistance = 45;
    input.connect(lp);
    lp.connect(panner);
    panner.connect(this.worldBus);
    const slot: PoolSlot = {
      input,
      lp,
      panner,
      occlusion: 0,
      current: 0,
      freeAt: 0,
      set(x: number, y: number, z: number) {
        if (panner.positionX) {
          panner.positionX.value = x;
          panner.positionY.value = y;
          panner.positionZ.value = z;
        } else {
          (panner as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(x, y, z);
        }
      },
    };
    return slot;
  }

  /** Wall-clock in context time. Everything schedules relative to this. */
  get now(): number {
    return this.ctx.currentTime;
  }

  get running(): boolean {
    return this.ctx.state === 'running';
  }

  /**
   * What the mix is doing right now, as numbers.
   *
   * Exists for the same reason the histogram behind every screenshot does: I
   * cannot hear this, so "audio works" has to be something a harness can assert.
   * `voices` in particular is the one that catches the real failure — a pool
   * that has silently filled up and started stealing slots mid-footstep reads as
   * clicking to a player and as a number greater than the pool size here.
   */
  get stats(): {
    running: boolean;
    voices: number;
    named: number;
    bed: boolean;
    time: number;
    total: number;
    fired: Record<string, number>;
  } {
    const now = this.now;
    let voices = 0;
    for (const s of this.slots) if (s.freeAt > now) voices++;
    let total = 0;
    const fired: Record<string, number> = {};
    for (const [k, v] of this.fired) {
      fired[k] = v;
      total += v;
    }
    return { running: this.running, voices, named: this.named.size, bed: this.started, time: now, total, fired };
  }

  set volume(v: number) {
    this.master.gain.setTargetAtTime(Math.max(0, Math.min(1, v)), this.now, 0.05);
  }

  /**
   * A stable emitter for something that keeps making noise and keeps moving.
   *
   * Guards get one each, for their whole life. A pooled slot would be reassigned
   * mid-footstep the moment twenty-four other things happened, and a panner that
   * jumps across the room while a source is still connected to it is a very
   * distinctive, very wrong noise.
   */
  emitter(key: string): Emitter {
    let slot = this.named.get(key);
    if (!slot) {
      slot = this.makeSlot();
      this.named.set(key, slot);
    }
    return slot;
  }

  /** Where the ears are. Fed the camera every frame. */
  listener(pos: Vector3, forward: Vector3, up: Vector3): void {
    const l = this.ctx.listener;
    const t = this.now;
    if (l.positionX) {
      // Smoothed, not set. A teleport in the harness or a respawn moves the
      // camera tens of metres in one frame, and a listener that follows it
      // instantly produces an audible zipper on every sustained sound.
      const k = 0.012;
      l.positionX.setTargetAtTime(pos.x, t, k);
      l.positionY.setTargetAtTime(pos.y, t, k);
      l.positionZ.setTargetAtTime(pos.z, t, k);
      l.forwardX.setTargetAtTime(forward.x, t, k);
      l.forwardY.setTargetAtTime(forward.y, t, k);
      l.forwardZ.setTargetAtTime(forward.z, t, k);
      l.upX.setTargetAtTime(up.x, t, k);
      l.upY.setTargetAtTime(up.y, t, k);
      l.upZ.setTargetAtTime(up.z, t, k);
    } else {
      const legacy = l as unknown as {
        setPosition(x: number, y: number, z: number): void;
        setOrientation(fx: number, fy: number, fz: number, ux: number, uy: number, uz: number): void;
      };
      legacy.setPosition(pos.x, pos.y, pos.z);
      legacy.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }
  }

  /** Ease every emitter's occlusion filter toward its target. Called per frame. */
  update(dt: number): void {
    const k = Math.min(1, dt * 6);
    const ease = (s: PoolSlot) => {
      if (Math.abs(s.current - s.occlusion) < 0.002) return;
      s.current += (s.occlusion - s.current) * k;
      // Logarithmic, because linear Hz spends nine tenths of its travel in a
      // range the ear cannot tell apart.
      s.lp.frequency.value = OPEN_HZ * (OCCLUDED_HZ / OPEN_HZ) ** s.current;
    };
    for (const s of this.slots) ease(s);
    for (const s of this.named.values()) ease(s);
  }

  private cueCtx(out: AudioNode, at = 0): CueCtx {
    // A hair in the future. Scheduling exactly at `currentTime` means the first
    // few milliseconds have already been rendered by the time the graph sees
    // them, and the attack of every percussive sound is clipped off.
    return { ctx: this.ctx, out, t: this.now + 0.005 + at, rng: this.rng };
  }

  /** A one-shot in the player's own head: their footsteps, their hands, the UI. */
  play(id: CueId, gain = 1, at = 0): void {
    if (!this.running) return;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    g.connect(this.head);
    this.fired.set(id, (this.fired.get(id) ?? 0) + 1);
    ONE_SHOTS[id](this.cueCtx(g, at));
  }

  /** A one-shot somewhere in the building. */
  playAt(id: CueId, x: number, y: number, z: number, occlusion = 0, gain = 1): void {
    if (!this.running) return;
    const slot = this.claim();
    slot.set(x, y, z);
    slot.occlusion = occlusion;
    slot.current = occlusion;
    slot.lp.frequency.value = OPEN_HZ * (OCCLUDED_HZ / OPEN_HZ) ** occlusion;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    g.connect(slot.input);
    this.fired.set(id, (this.fired.get(id) ?? 0) + 1);
    const dur = ONE_SHOTS[id](this.cueCtx(g));
    slot.freeAt = this.now + dur;
  }

  /** A one-shot on an emitter that belongs to somebody, and that is moving. */
  playOn(em: Emitter, id: CueId, gain = 1): void {
    if (!this.running) return;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    g.connect(em.input);
    this.fired.set(id, (this.fired.get(id) ?? 0) + 1);
    ONE_SHOTS[id](this.cueCtx(g));
  }

  private claim(): PoolSlot {
    const now = this.now;
    let oldest = this.slots[0];
    for (const s of this.slots) {
      if (s.freeAt <= now) return s;
      if (s.freeAt < oldest.freeAt) oldest = s;
    }
    // Every slot busy. Stealing the one that finishes soonest is the least bad
    // option; the alternative is dropping the sound, and a dropped alarm is
    // worse than a clipped footstep.
    return oldest;
  }

  /** Start a tool loop in the player's hands. Returns null for silent verbs. */
  startWork(kind: WorkKind): Loop | null {
    if (!this.running) return null;
    return work(this.cueCtx(this.head), kind);
  }

  /** Start a loop on a world emitter. */
  startWorkAt(em: Emitter, kind: WorkKind): Loop | null {
    if (!this.running) return null;
    return work(this.cueCtx(em.input), kind);
  }

  /** The room tone. Started once, never stopped. */
  startBed(): void {
    if (this.bedLoop || !this.running) return;
    this.bedLoop = bed(this.cueCtx(this.bedBus));
    this.started = true;
  }

  /** 0 shop floor, 1 back of house. */
  setBed(back: number): void {
    this.bedLoop?.set(Math.max(0, Math.min(1, back)));
  }

  get bedRunning(): boolean {
    return this.started;
  }

  /** Pull everything down without stopping it — the pause card and the results. */
  duck(amount: number, seconds = 0.15): void {
    this.master.gain.setTargetAtTime(0.9 * (1 - amount), this.now, seconds);
  }
}
